/**
 * <s-scroller> — horizontal product slider built on native scroll snapping.
 * Swipe works with no JavaScript; this element only adds the prev/next buttons
 * (command="--prev" / "--next" with commandfor pointing at this element) and
 * disables each button at the start or end of the track.
 */
class SScroller extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const track = this.querySelector('[data-scroller-track]');
    if (!track) return;

    this.addEventListener(
      'command',
      (e) => {
        if (e.command !== '--next' && e.command !== '--prev') return;
        const rtl = getComputedStyle(track).direction === 'rtl';
        let dir = e.command === '--next' ? 1 : -1;
        if (rtl) dir *= -1;
        track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
      },
      opts,
    );

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const pos = Math.abs(track.scrollLeft);
      this.querySelectorAll(`[commandfor="${this.id}"]`).forEach((btn) => {
        const atEdge = btn.getAttribute('command') === '--prev' ? pos <= 2 : pos >= max - 2;
        btn.disabled = atEdge;
      });
      const dots = this.querySelectorAll('[data-scroller-dot]');
      if (dots.length && track.clientWidth > 0) {
        const active = pos >= max - 2 ? dots.length - 1 : Math.min(dots.length - 1, Math.round(pos / track.clientWidth));
        dots.forEach((dot, i) => {
          dot.classList.toggle('bg-on-surface', i === active);
          dot.classList.toggle('bg-border', i !== active);
        });
      }
    };
    track.addEventListener('scroll', update, { ...opts, passive: true });
    window.addEventListener('resize', update, opts);
    update();

    this.#setupAutoplay(track, opts);
  }

  /**
   * Optional autoplay: add autoplay="5000" (ms). Runs only while the slider is on
   * screen and the tab is visible, pauses on hover/focus, stops for good once the
   * shopper touches, swipes, scrolls or uses the arrows, and never runs when the
   * visitor prefers reduced motion.
   */
  #setupAutoplay(track, opts) {
    const delay = parseInt(this.getAttribute('autoplay'), 10);
    if (!delay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let timer = null;
    let onScreen = false;
    let hovered = false;
    let stopped = false;

    const advance = () => {
      const max = track.scrollWidth - track.clientWidth;
      if (max <= 2) return;
      const pos = Math.abs(track.scrollLeft);
      const rtl = getComputedStyle(track).direction === 'rtl';
      if (pos >= max - 2) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: (rtl ? -1 : 1) * track.clientWidth, behavior: 'smooth' });
      }
    };
    const sync = () => {
      const shouldRun = !stopped && onScreen && !hovered && !document.hidden;
      if (shouldRun && !timer) timer = setInterval(advance, delay);
      if (!shouldRun && timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const stop = () => {
      stopped = true;
      sync();
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
        sync();
      },
      { threshold: 0.5 },
    );
    observer.observe(track);
    opts.signal.addEventListener('abort', () => {
      observer.disconnect();
      if (timer) clearInterval(timer);
    });

    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown', 'command']) {
      this.addEventListener(type, stop, { ...opts, passive: true });
    }
    this.addEventListener('focusin', stop, opts);
    this.addEventListener('mouseenter', () => { hovered = true; sync(); }, opts);
    this.addEventListener('mouseleave', () => { hovered = false; sync(); }, opts);
    document.addEventListener('visibilitychange', sync, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-scroller')) customElements.define('s-scroller', SScroller);
